import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

export default class extends Controller {
  static targets = ["container"]

  // Demo data showing a fake personal development collection
  demoData = {
    nodes: [
      // Central collection
      { id: "col1", type: "collection", label: "Personal Development" },

      // Books
      { id: "b1", type: "book", label: "Think and Grow Rich" },
      { id: "b2", type: "book", label: "Atomic Habits" },
      { id: "b3", type: "book", label: "The 7 Habits" },
      { id: "b4", type: "book", label: "How to Win Friends" },
      { id: "b5", type: "book", label: "The Alchemist" },
      { id: "b6", type: "book", label: "Rich Dad Poor Dad" },

      // Authors
      { id: "a1", type: "author", label: "Napoleon Hill" },
      { id: "a2", type: "author", label: "James Clear" },
      { id: "a3", type: "author", label: "Stephen Covey" },
      { id: "a4", type: "author", label: "Dale Carnegie" },
      { id: "a5", type: "author", label: "Paulo Coelho" },
      { id: "a6", type: "author", label: "Robert Kiyosaki" },

      // Tags
      { id: "t1", type: "tag", label: "Mindset" },
      { id: "t2", type: "tag", label: "Habits" },
      { id: "t3", type: "tag", label: "Leadership" },
      { id: "t4", type: "tag", label: "Finance" },

      // Publisher
      { id: "p1", type: "publisher", label: "Penguin Books" },
    ],
    links: [
      // Books in collection
      { source: "col1", target: "b1", relation: "contains" },
      { source: "col1", target: "b2", relation: "contains" },
      { source: "col1", target: "b3", relation: "contains" },
      { source: "col1", target: "b4", relation: "contains" },
      { source: "col1", target: "b5", relation: "contains" },
      { source: "col1", target: "b6", relation: "contains" },

      // Authors wrote books
      { source: "a1", target: "b1", relation: "wrote" },
      { source: "a2", target: "b2", relation: "wrote" },
      { source: "a3", target: "b3", relation: "wrote" },
      { source: "a4", target: "b4", relation: "wrote" },
      { source: "a5", target: "b5", relation: "wrote" },
      { source: "a6", target: "b6", relation: "wrote" },

      // Tags on books
      { source: "t1", target: "b1", relation: "tagged" },
      { source: "t1", target: "b5", relation: "tagged" },
      { source: "t2", target: "b2", relation: "tagged" },
      { source: "t2", target: "b3", relation: "tagged" },
      { source: "t3", target: "b3", relation: "tagged" },
      { source: "t3", target: "b4", relation: "tagged" },
      { source: "t4", target: "b1", relation: "tagged" },
      { source: "t4", target: "b6", relation: "tagged" },

      // Publisher
      { source: "p1", target: "b5", relation: "published" },
      { source: "p1", target: "b2", relation: "published" },
    ]
  }

  connect() {
    this.setupIntersectionObserver()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.resizeObserver) this.resizeObserver.disconnect()
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.initialized) {
          this.initialized = true
          this.renderGraph()
          this.bindResize()
          this.observer.disconnect()
        }
      })
    }, { threshold: 0.1 })
    this.observer.observe(this.element)
  }

  renderGraph() {
    const container = this.containerTarget
    const width = container.clientWidth
    const height = container.clientHeight || 400

    // Clear existing content
    d3.select(container).selectAll("*").remove()

    // Deep copy the data to avoid mutation issues on re-render
    const data = {
      nodes: this.demoData.nodes.map(n => ({ ...n })),
      links: this.demoData.links.map(l => ({ ...l }))
    }

    // Create SVG
    const svg = d3.select(container)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])

    // Create a group for zoom
    const g = svg.append("g")

    // Define zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on("zoom", (event) => {
        g.attr("transform", event.transform)
      })

    svg.call(zoom)

    // Initial zoom to fit content nicely
    svg.call(zoom.transform, d3.zoomIdentity.translate(width * 0.1, height * 0.05).scale(0.85))

    // Create simulation
    const simulation = d3.forceSimulation(data.nodes)
      .force("link", d3.forceLink(data.links).id(d => d.id).distance(90))
      .force("charge", d3.forceManyBody().strength(-250))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(d => this.nodeRadius(d) + 8))

    // Create links
    const link = g.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(data.links)
      .join("line")
      .attr("stroke", "#8b7355")
      .attr("stroke-width", 1.5)
      .attr("stroke-opacity", 0.4)

    // Create nodes
    const node = g.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(data.nodes)
      .join("g")
      .call(d3.drag()
        .on("start", (event, d) => this.dragStarted(event, d, simulation))
        .on("drag", (event, d) => this.dragged(event, d))
        .on("end", (event, d) => this.dragEnded(event, d, simulation)))
      .style("cursor", "grab")

    // Add circles to nodes
    node.append("circle")
      .attr("r", d => this.nodeRadius(d))
      .attr("fill", d => this.nodeColor(d))
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .attr("stroke-opacity", 0.8)

    // Add labels
    node.append("text")
      .text(d => this.truncateLabel(d.label))
      .attr("x", 0)
      .attr("y", d => this.nodeRadius(d) + 14)
      .attr("text-anchor", "middle")
      .attr("fill", "#d4c4a8")
      .attr("font-family", "Arial, sans-serif")
      .attr("font-size", "10px")
      .attr("font-weight", "500")

    // Update positions on tick
    simulation.on("tick", () => {
      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y)

      node.attr("transform", d => `translate(${d.x},${d.y})`)
    })

    this.simulation = simulation
  }

  nodeRadius(node) {
    switch (node.type) {
      case "book":
        return 20
      case "collection":
        return 26
      case "publisher":
        return 16
      case "author":
        return 16
      case "tag":
        return 12
      default:
        return 14
    }
  }

  nodeColor(node) {
    const colors = {
      book: "#352a20",
      author: "#b07d2e",
      publisher: "#6b5642",
      collection: "#8b7355",
      tag: "#4a6e5f",
      memorabilia: "#964a33"
    }
    return colors[node.type] || "#352a20"
  }

  truncateLabel(label) {
    return label.length > 18 ? label.substring(0, 15) + "..." : label
  }

  dragStarted(event, d, simulation) {
    if (!event.active) simulation.alphaTarget(0.3).restart()
    d.fx = d.x
    d.fy = d.y
  }

  dragged(event, d) {
    d.fx = event.x
    d.fy = event.y
  }

  dragEnded(event, d, simulation) {
    if (!event.active) simulation.alphaTarget(0)
    d.fx = null
    d.fy = null
  }

  bindResize() {
    this.resizeObserver = new ResizeObserver(() => {
      this.renderGraph()
    })
    this.resizeObserver.observe(this.containerTarget)
  }
}
